import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    Renderer2
} from '@angular/core';

@Component({
    selector: 'coach-mark',
    template: `
        @if (visible) {
            <div class="coach-mark {{ placement }}">
                <div class="coach-mark-arrow {{ arrowDirection }}"></div>
                <div class="coach-mark-inner">
                    <ng-content></ng-content>
                    {{ content }}
                </div>
            </div>
        }

        <!-- [style.top]="top + 'px'"
        [style.left]="left + 'px'" -->
    `,
    standalone: false
})
export class CoachMarkComponent implements AfterViewInit {
    @Input()
    hostElement: HTMLElement;
    @Input()
    content: string;
    @Input()
    placement: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
    @Input()
    arrowDirection: 'left' | 'right' = 'right';
    @Input()
    top = 0;
    @Input()
    left = 0;

    visible = false;
    maxWidth: number = null;

    constructor(
        private element: ElementRef,
        private cdr: ChangeDetectorRef,
        private renderer: Renderer2
    ) {}

    ngAfterViewInit(): void {
        this.show();
        this.cdr.detectChanges();
    }

    show(): void {
        if (!this.hostElement) {
            return;
        }

        if (this.visible) {
            return;
        }

        this.visible = true;

        // Create arrow above/below coach mark content depending on position.
        this.createCoachMarkArrow(
            this.element.nativeElement,
            this.placement,
            this.arrowDirection
        );

        const p = this.positionElements(
            this.hostElement,
            this.element.nativeElement,
            this.placement
        );
        // this.top = p.top;
        // this.left = p.left;
    }

    hide(): void {
        this.visible = false;
    }

    createCoachMarkArrow(
        element: HTMLElement,
        coachMarkPlacement: string,
        arrowDirection: string
    ) {
        const coachMarkDiv = element.querySelectorAll('.coach-mark');
        const coachMarkInnerDiv = element.querySelectorAll('.coach-mark-inner');
        const arrowDiv = this.renderer.createElement('div');
        this.renderer.setProperty(
            arrowDiv,
            'class',
            `coach-mark-arrow ${arrowDirection}`
        );

        switch (coachMarkPlacement) {
            case 'bottom':
                this.renderer.insertBefore(
                    coachMarkDiv,
                    coachMarkInnerDiv,
                    arrowDiv
                );
                break;
            case 'top':
            default:
                coachMarkDiv[0].appendChild(arrowDiv);
                break;
        }
    }

    private positionElements(
        hostEl: HTMLElement,
        targetEl: HTMLElement,
        positionStr: string,
        appendToBody = false
    ): { top: number; left: number } {
        const positionStrParts = positionStr.split('-');
        const pos0 = positionStrParts[0];
        const pos1 = positionStrParts[1] || 'center';
        const hostElPos = appendToBody
            ? this.offset(hostEl)
            : this.position(hostEl);
        this.maxWidth = parseInt(
            this.getStyle(this.element.nativeElement, 'max-width')
        );
        const targetElWidth = this.maxWidth || targetEl.offsetWidth;
        const targetElHeight = targetEl.offsetHeight;
        const shiftWidth: any = {
            center: (): number =>
                hostElPos.left + hostElPos.width / 2 - targetElWidth / 2,
            left: (): number => hostElPos.left,
            right: (): number => hostElPos.left + hostElPos.width
        };

        const shiftHeight: any = {
            center: (): number =>
                hostElPos.top + hostElPos.height / 2 - targetElHeight / 2,
            top: (): number => hostElPos.top,
            bottom: (): number => hostElPos.top + hostElPos.height
        };

        let targetElPos: { top: number; left: number };
        switch (pos0) {
            case 'right':
                targetElPos = {
                    top: shiftHeight[pos1](),
                    left: shiftWidth[pos0]()
                };
                break;

            case 'left':
                targetElPos = {
                    top: shiftHeight[pos1](),
                    left: hostElPos.left - targetElWidth
                };
                break;

            case 'bottom':
            case 'center':
                targetElPos = {
                    top: shiftHeight[pos0](),
                    left: shiftWidth[pos1]()
                };
                break;

            default:
                targetElPos = {
                    top: hostElPos.top - targetElHeight,
                    left: shiftWidth[pos1]()
                };
                break;
        }

        return targetElPos;
    }

    private position(nativeEl: HTMLElement): {
        width: number;
        height: number;
        top: number;
        left: number;
    } {
        let offsetParentBCR = { top: 0, left: 0 };
        const elBCR = this.offset(nativeEl);
        const offsetParentEl = this.parentOffsetEl(nativeEl);
        if (offsetParentEl !== window.document) {
            offsetParentBCR = this.offset(offsetParentEl);
            offsetParentBCR.top +=
                offsetParentEl.clientTop - offsetParentEl.scrollTop;
            offsetParentBCR.left +=
                offsetParentEl.clientLeft - offsetParentEl.scrollLeft;
        }

        const boundingClientRect = nativeEl.getBoundingClientRect();
        return {
            width: boundingClientRect.width || nativeEl.offsetWidth,
            height: boundingClientRect.height || nativeEl.offsetHeight,
            top: elBCR.top - offsetParentBCR.top,
            left: elBCR.left - offsetParentBCR.left
        };
    }

    private offset(nativeEl: any): {
        width: number;
        height: number;
        top: number;
        left: number;
    } {
        const boundingClientRect = nativeEl.getBoundingClientRect();
        return {
            width: boundingClientRect.width || nativeEl.offsetWidth,
            height: boundingClientRect.height || nativeEl.offsetHeight,
            top:
                boundingClientRect.top +
                (window.pageYOffset ||
                    window.document.documentElement.scrollTop),
            left:
                boundingClientRect.left +
                (window.pageXOffset ||
                    window.document.documentElement.scrollLeft)
        };
    }

    private getStyle(nativeEl: HTMLElement, cssProp: string): string {
        if ((nativeEl as any).currentStyle) {
            // IE
            return (nativeEl as any).currentStyle[cssProp];
        }

        if (window.getComputedStyle) {
            return (window.getComputedStyle(nativeEl) as any)[cssProp];
        }

        // finally try and get inline style
        return (nativeEl.style as any)[cssProp];
    }

    private isStaticPositioned(nativeEl: HTMLElement): boolean {
        return (this.getStyle(nativeEl, 'position') || 'static') === 'static';
    }

    private parentOffsetEl(nativeEl: HTMLElement): any {
        let offsetParent: any = nativeEl.offsetParent || window.document;
        while (
            offsetParent &&
            offsetParent !== window.document &&
            this.isStaticPositioned(offsetParent)
        ) {
            offsetParent = offsetParent.offsetParent;
        }
        return offsetParent || window.document;
    }

    private computeMaxWidth(nativeEl: HTMLElement): number {
        const childWidth = nativeEl.children[0].clientWidth;
        let computedMaxWidth = childWidth;

        const maxChildWidth = this.getMaxWidthOfChildElements(
            nativeEl.children[0] as HTMLElement
        );
        if (maxChildWidth > childWidth) {
            const padding = parseInt(
                this.getStyle(
                    nativeEl.querySelector('.tooltip-inner'),
                    'padding-right'
                )
            );
            computedMaxWidth = maxChildWidth + padding;
        }

        return computedMaxWidth;
    }

    private getMaxWidthOfChildElements(targetEl: HTMLElement): number {
        let maxWidth = targetEl.offsetWidth;

        Array.from(targetEl.children).forEach(function (child) {
            if (child.scrollWidth > maxWidth) {
                maxWidth = child.scrollWidth;
            }
        });

        return maxWidth;
    }
}
